import React, { useEffect, useState, useRef } from "react";
import { io } from "socket.io-client";
import axios from "axios";
import ReactQuill from "react-quill";
import {
  Search,
  Trash2,
  MoreHorizontal,
  Send,
  Image,
  Paperclip,
  Smile,
  ChevronLeft,
  Menu,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ModernMessageInput from "./ModernMessageInput";

const BackendUrl = process.env.REACT_APP_Backend_Url;
const socket = io(BackendUrl);

const formatMessageDate = (dateString) => {
  if (!dateString) return "Date inconnue";

  const messageDate = new Date(dateString);
  if (isNaN(messageDate.getTime())) {
    console.error("Date invalide:", dateString);
    return "Date invalide";
  }

  const now = new Date();
  const diffTime = Math.abs(now - messageDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "Aujourd'hui";
  } else if (diffDays === 1) {
    return "Hier";
  } else if (diffDays <= 7) {
    return messageDate.toLocaleDateString("fr-FR", { weekday: "long" });
  } else {
    return messageDate.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
};

const formatLastMessagePreview = (message) => {
  // Enlever les balises HTML du message
  const strippedMessage = message.replace(/<[^>]+>/g, "");
  return strippedMessage.length > 30
    ? strippedMessage.substring(0, 30) + "..."
    : strippedMessage;
};

const groupMessagesByDate = (messages) => {
  const groups = {};
  messages?.forEach((message) => {
    const date = formatMessageDate(message.date || new Date());
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
  });
  return groups;
};

const quillModules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

// Couleurs du drapeau du Niger
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

const quillFormats = ["bold", "italic", "underline", "list", "bullet", "link"];
const admin = JSON.parse(localStorage.getItem("AdminEcomme"));
function Inbox({ setUnreadCounts }) {
  const [allUsers, setAllUsers] = useState(null);
  const [allProfiles, setallprofiles] = useState(null);
  const [allMessage, setAllMessage] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [userSearch, setUserSearch] = useState(null);
  const [message, setMessage] = useState("");
  const [searchName, setSearchName] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);

  const messageContainerRef = useRef(null);
  const provenance = false;
  if (admin) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${admin.token}`;
  }
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
      setShowSidebar(window.innerWidth >= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  }, [allMessage]);

  const handleMessageChange = (value) => {
    setMessage(value);
  };
  useEffect(() => {
    axios
      .get(`${BackendUrl}/api/user/getUsers`)
      .then((users) => {
        setAllUsers(users.data?.user);
        // console.log(users.data.data);
      })
      .catch((error) => console.log(error));
    axios
      .get(`${BackendUrl}/api/profilesRoutes/all`)
      .then((users) => {
        setallprofiles(users.data.data);
      })
      .catch((error) => console.log(error));

    axios
      .get(`${BackendUrl}/api/messagesRoutes`)
      .then((res) => {
        setAllMessages(res.data);
        setUnreadCounts(
          res.data.filter(
            (item) => item.lusAdmin === false && item.provenance === true
          ).length || 0
        );
        // console.log(res.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    // Écouter les nouveaux messages du serveur
    socket.on("new_message_user", (message) => {
      axios
        .get(`${BackendUrl}/api/messagesRoutes`)
        .then((res) => {
          setAllMessages(res.data);
        })
        .catch((error) => {
          console.log(error);
        });
      if (message) {
        axios
          .get(`${BackendUrl}/api/messagesRoutes/user/${message.data.clefUser}`)
          .then((res) => {
            setAllMessage(res.data);
          })
          .catch((error) => {
            console.log(error);
          });
      }
    });

    return () => {
      // Nettoyer l'écouteur du socket lors du démontage du composant
      socket.off("new_message_user");
    };
  }, []); // Retirez socket des dépendances

  const getMessages = (param) => {
    axios
      .get(`${BackendUrl}/api/messagesRoutes/user/${param}`)
      .then((res) => {
        setAllMessage(res.data);
      })
      .catch((error) => {
        console.log(error);
      });
    axios
      .post(`${BackendUrl}/api/messagesRoutes/lecture-admin`, {
        userKey: param,
      })
      .then((resp) => {
        // console.log(resp);
      })
      .catch((erro) => {
        console.log(erro);
      });
    axios
      .get(`${BackendUrl}/api/messagesRoutes`)
      .then((res2) => {
        setAllMessages(res2.data);
        // console.log(res.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };
  // getAllUserMessages
  const envoyer = (e) => {
    e.preventDefault();
    if (!message.length) {
      return;
    }
    axios
      .post(`${BackendUrl}/api/messagesRoutes/create`, {
        message: message,
        clefUser: selectedUser?._id, // Remplacé istrue par selectedUser
        provenance: provenance,
      })
      .then((res) => {
        socket.emit("new_message_u", {
          data: {
            message: message,
            clefUser: selectedUser?._id, // Remplacé istrue par selectedUser
            provenance: provenance,
          },
        });
        setMessage("");
        axios
          .get(`${BackendUrl}/api/messagesRoutes/user/${selectedUser?._id}`) // Remplacé istrue par selectedUser
          .then((re) => {
            setAllMessage(re.data);
          })
          .catch((erro) => {
            console.log(erro);
          });
      })
      .catch((error) => console.log(error));
  };

  const searchByName = (name) => {
    if (name.length < 2) {
      return;
    }
    axios
      .get(`${BackendUrl}/api/user/getUserByName/${name}`)
      .then((res) => {
        setUserSearch(res.data.users);
      })
      .catch((error) => {
        // console.log(error);
        setUserSearch(null);
      });
  };

  const deletmessage = (param) => {
    axios
      .delete(`${BackendUrl}/api/messagesRoutes/${param}`)
      .then((res) => {
        alert(res.data.message);
        socket.emit("delete_message", res.data.message);

        axios
          .get(`${BackendUrl}/api/messagesRoutes/user/${selectedUser?._id}`) // Correction ici
          .then((re) => {
            setAllMessage(re.data);
          })
          .catch((erro) => {
            console.log(erro);
          });
      })
      .catch((error) => console.log(error));
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    getMessages(user._id);
    if (isMobileView) {
      setShowSidebar(false);
    }
  };

  const handleBackToList = () => {
    setSelectedUser(null);
    setShowSidebar(true);
  };

  // Styles personnalisés pour ReactQuill
  const customQuillStyles = {
    quillWrapper:
      "bg-background rounded-lg border border-input focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
    quillEditor: "min-h-[40px] max-h-[120px] overflow-y-auto",
    quillToolbar: "border-b border-input bg-muted/50 rounded-t-lg",
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar avec animation */}
      <div
        className={`fixed md:relative md:w-80 border-r flex flex-col bg-white shadow-md transition-all duration-300 z-10 h-full
        ${
          showSidebar
            ? "w-full left-0"
            : "w-full -left-full md:left-0 md:w-0 md:opacity-0 md:m-0 md:p-0"
        }`}
      >
        <div
          className="p-4 border-b flex items-center justify-between"
          style={{ backgroundColor: COLORS.paleGreen }}
        >
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher..."
              className="pl-8 rounded-full border-2 focus-visible:ring-2 focus-visible:ring-offset-0 focus-visible:ring-offset-green-50 focus-visible:ring-green-600 focus-visible:border-green-600"
              value={searchName}
              onChange={(e) => {
                searchByName(e.target.value);
                setSearchName(e.target.value);
              }}
              style={{ borderColor: COLORS.green }}
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="ml-2 md:hidden text-green-700 hover:bg-green-50"
            onClick={() => setShowSidebar(false)}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        </div>

        <ScrollArea className="flex-1">
          {(!userSearch || searchName.length < 2 ? allUsers : userSearch)?.map(
            (user) => {
              const userMessages = allMessages.filter(
                (msg) => msg.clefUser === user._id
              );
              const lastMessage = userMessages[userMessages.length - 1];
              const unreadCount = allMessages.filter(
                (item) =>
                  item.clefUser === user._id &&
                  item.lusAdmin === false &&
                  item.provenance === true
              ).length;

              return (
                <div
                  key={user._id}
                  className={`p-4 hover:bg-accent cursor-pointer transition-colors border-b ${
                    selectedUser?._id === user._id
                      ? `bg-gradient-to-r from-green-50 to-green-100 border-l-4`
                      : "border-l-4 border-l-transparent"
                  }`}
                  style={{
                    borderLeftColor:
                      selectedUser?._id === user._id
                        ? COLORS.green
                        : "transparent",
                  }}
                  onClick={() => {
                    handleUserSelect(user);
                    if (isMobileView) setShowSidebar(false);
                  }}
                >
                  <div className="flex items-center space-x-4">
                    <Avatar
                      className="border-2"
                      style={{ borderColor: COLORS.lightGreen }}
                    >
                      <AvatarImage
                        src={
                          allProfiles?.find(
                            (prof) => prof.clefUser === user._id
                          )?.image
                        }
                        alt={user.name}
                      />
                      <AvatarFallback
                        style={{
                          backgroundColor: COLORS.orange,
                          color: COLORS.white,
                        }}
                      >
                        {user.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <h4 className="text-sm font-medium truncate">
                          {user.name}
                        </h4>
                        {lastMessage && (
                          <span className="text-xs text-muted-foreground bg-gray-100 px-2 py-0.5 rounded-full">
                            {new Date(lastMessage.createdAt).toLocaleTimeString(
                              "fr-FR",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </span>
                        )}
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <p className="text-sm text-muted-foreground truncate">
                          {lastMessage
                            ? formatLastMessagePreview(lastMessage.message)
                            : "Aucun message"}
                        </p>
                        {unreadCount > 0 && (
                          <Badge
                            style={{
                              backgroundColor: COLORS.orange,
                              color: COLORS.white,
                            }}
                            className="rounded-full"
                          >
                            {unreadCount}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
          )}
          <ScrollBar />
        </ScrollArea>
      </div>

      {/* Zone de chat */}
      <div className="flex-1 flex flex-col relative">
        {/* Bouton afficher liste pour mobile quand sidebar caché */}
        {!showSidebar && !selectedUser && isMobileView && (
          <Button
            variant="outline"
            className="absolute top-4 left-4 z-20"
            onClick={() => setShowSidebar(true)}
            style={{ borderColor: COLORS.green, color: COLORS.green }}
          >
            <Menu className="h-5 w-5 mr-2" />
            Conversations
          </Button>
        )}

        {selectedUser ? (
          <>
            <div
              className="p-4 border-b flex justify-between items-center shadow-sm"
              style={{ backgroundColor: COLORS.paleGreen }}
            >
              <div className="flex items-center space-x-4">
                {!showSidebar && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowSidebar(true)}
                    className="md:hidden"
                  >
                    <Menu className="h-5 w-5" style={{ color: COLORS.green }} />
                  </Button>
                )}
                <Avatar
                  className="border-2"
                  style={{ borderColor: COLORS.orange }}
                >
                  <AvatarImage
                    src={
                      allProfiles?.find(
                        (prof) => prof.clefUser === selectedUser._id
                      )?.image
                    }
                    alt={selectedUser.name}
                  />
                  <AvatarFallback
                    style={{
                      backgroundColor: COLORS.orange,
                      color: COLORS.white,
                    }}
                  >
                    {selectedUser.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h4 className="font-medium">{selectedUser.name}</h4>
                  <p className="text-sm text-muted-foreground">
                    {allProfiles?.find(
                      (prof) => prof.clefUser === selectedUser._id
                    )?.numero ||
                      selectedUser?.phoneNumber ||
                      "Pas de numéro"}
                  </p>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    style={{ color: COLORS.green }}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>
                    Rechercher dans la conversation
                  </DropdownMenuItem>
                  <DropdownMenuItem>Supprimer la conversation</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <ScrollArea
              className="flex-1 p-4 bg-gradient-to-b from-green-50 to-white"
              ref={messageContainerRef}
            >
              <div className="space-y-6 pb-4">
                {Object.entries(groupMessagesByDate(allMessage))?.map(
                  ([date, messages]) => (
                    <div key={date} className="space-y-4">
                      <div className="flex justify-center">
                        <Badge
                          variant="secondary"
                          className="text-xs px-3 py-1 rounded-full shadow-sm"
                          style={{
                            backgroundColor: COLORS.lightGray,
                            color: COLORS.gray,
                          }}
                        >
                          {date}
                        </Badge>
                      </div>
                      {messages?.map((msg, index) => (
                        <div
                          key={index}
                          className={`flex ${
                            msg.provenance ? "justify-start" : "justify-end"
                          } mb-2`}
                        >
                          <div
                            className={`flex max-w-[85%] md:max-w-[70%] gap-2 ${
                              msg.provenance ? "flex-row" : "flex-row-reverse"
                            }`}
                          >
                            {msg.provenance && (
                              <Avatar className="w-8 h-8">
                                <AvatarImage
                                  src={
                                    allProfiles?.find(
                                      (prof) =>
                                        prof.clefUser === selectedUser._id
                                    )?.image
                                  }
                                  alt={selectedUser.name}
                                />
                                <AvatarFallback
                                  style={{
                                    backgroundColor: COLORS.orange,
                                    color: COLORS.white,
                                  }}
                                >
                                  {selectedUser.name.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                            )}
                            <div className="space-y-1">
                              <Card
                                className={`rounded-2xl shadow-sm ${
                                  msg.provenance
                                    ? "rounded-tl-none"
                                    : "rounded-tr-none"
                                }`}
                                style={{
                                  backgroundColor: msg.provenance
                                    ? COLORS.white
                                    : COLORS.green,
                                  color: msg.provenance
                                    ? COLORS.gray
                                    : COLORS.white,
                                  borderColor: msg.provenance
                                    ? COLORS.lightGray
                                    : COLORS.green,
                                }}
                              >
                                <CardContent className="p-3">
                                  <div
                                    className="text-sm break-words"
                                    dangerouslySetInnerHTML={{
                                      __html: msg.message,
                                    }}
                                  />
                                </CardContent>
                              </Card>
                              <div
                                className={`flex items-center gap-2 text-xs text-muted-foreground ${
                                  msg.provenance
                                    ? "justify-start"
                                    : "justify-end"
                                }`}
                              >
                                <span>
                                  {new Date(msg.date).toLocaleTimeString(
                                    "fr-FR",
                                    {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    }
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
              <ScrollBar />
            </ScrollArea>
            <div className="p-4 border-t bg-white">
              {/* <form onSubmit={envoyer} className="space-y-4"> */}
              {/* Remplacer tout ce bloc */}
              <ModernMessageInput
                message={message}
                handleMessageChange={handleMessageChange}
                envoyer={envoyer}
              />
              {/* </form> */}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-4">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: COLORS.lightGreen }}
            >
              <Send className="h-8 w-8" style={{ color: COLORS.green }} />
            </div>
            <h3
              className="text-xl font-medium mb-2"
              style={{ color: COLORS.green }}
            >
              Bienvenue dans votre messagerie
            </h3>
            <p className="text-center max-w-md">
              Sélectionnez une conversation dans la liste pour commencer à
              discuter
            </p>
            {isMobileView && (
              <Button
                className="mt-6"
                onClick={() => setShowSidebar(true)}
                style={{ backgroundColor: COLORS.green, color: COLORS.white }}
              >
                <Menu className="h-5 w-5 mr-2" />
                Voir les conversations
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Inbox;
